import React, { useEffect, useContext } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { motion, useAnimation } from "framer-motion"
import { Link } from "gatsby"

import Context from "context/"
import ContentWrapper from "styles/contentWrapper"
import SplashScreen from "components/splashScreen"
import { lightTheme, darkTheme } from "styles/theme"

const StyledSection = styled.section`
  width: 100%;
  height: auto;
  background-image: url("https://hrflow-ai.imgix.net/backgrounds/main-spot.svg"), url("https://hrflow-ai.imgix.net/gradient-bg.webp");
  background-repeat: no-repeat;
  background-attachment: scroll;
  background-size: 29rem 25rem, cover;
  background-position: 68% 0, center;
  padding: 3.5rem 0;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-bottom: 0;
  }

`

const StyledContentWrapper = styled(ContentWrapper)`
  && {
    width: 100%;
    height: 100%;
    min-height: 60vh;
    display: flex;
    flex-direction: column-reverse;
    justify-content: center;
    .video {
      margin: 4rem 0 0 0;
    }
    .intro {
      margin-top: 4rem;
      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        margin-top: 0;
      }
      .title {
        margin-bottom: 2rem;
        color: white;
        @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
          font-size: 2.5rem;
        }
      }
      .subtitle {
        color: white;
        font-family: ${({ theme }) => theme.fonts.plainSkeleton};
        font-size: 1.3rem;
        padding: 0;
      }
      .subtitlePrefix {
        color: white;
        margin-bottom: 1rem;
        font-size: 1.3rem;
        padding: 0;
      }
      text-align: center;
      @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
        text-align: left;
      }
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-gap: 2rem;
      align-items: center;
      .video {
        margin: 7rem 0 3rem 0;
        justify-self: center;
        margin-top: 4.5rem;
        max-width: 30rem;
        margin-bottom: 2rem;    
      }
    }
  }
`
const StyledButtons = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    justify-content: flex-start;
  }
  .cta-btn {
    width: auto;
    height: auto;
    font-weight: 700;
    white-space: nowrap;
    text-align: center;
    color: ${({ theme }) => theme.colors.primary};
    text-transform: uppercase;
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px solid ${({ theme }) => theme.colors.white};
    background: ${({ theme }) => theme.colors.white};
    transition: 20ms ease-out;
    font-size: 1rem;
    padding: 0.7rem 1rem 0.8rem 1rem;
    margin: 0;
    &:hover {
      background-color: rgba(255, 255, 255, 0.9);
    }
  }
  .cta-btn.empty {
    border: 1px solid ${({ theme }) => theme.colors.white};
    background: transparent;
    color: ${({ theme }) => theme.colors.white};
    margin-left: 1rem;
    &:hover {
      background-color: rgba(36, 204, 212, 0.4);
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.xs}) {
    flex-direction: column;
    .cta-btn:first-of-type {
      margin-bottom: 1rem;
    }
    .cta-btn.empty {
      margin-left: 0;
    }
  }
`

const Hero = ({ content }) => {
  const { frontmatter } = content[0].node
  const { isIntroDone, darkMode } = useContext(Context).state
  // Controls to orchestrate animations of greetings, emoji, social profiles, underlining
  const gControls = useAnimation()
  const eControls = useAnimation()
  const sControls = useAnimation()
  const uControls = useAnimation()

  // Start Animations after the splashScreen sequence is done
  useEffect(() => {
    const pageLoadSequence = async () => {
      if (isIntroDone) {
        eControls.start({
          rotate: [0, -10, 12, -10, 9, 0, 0, 0, 0, 0, 0],
          transition: { duration: 2.5, loop: 3, repeatDelay: 1 },
        })
        await gControls.start({
          opacity: 1,
          y: 0,
          transition: { delay: 0.4 },
        })
        await sControls.start({
          opacity: 1,
          x: 0,
        })
        // Animate underlining to hover state
        await uControls.start({
          boxShadow: `inset 0 -2rem 0 ${
            darkMode ? darkTheme.colors.secondary : lightTheme.colors.secondary
          }`,
          transition: { delay: 0.4, ease: "circOut" },
        })
      }
    }
    pageLoadSequence()
  }, [isIntroDone, darkMode, eControls, gControls, sControls, uControls])
  
  useEffect(() => {
    const script = document.createElement("script");

    script.src = "https://s3.amazonaws.com/img.riminder.net/animations/searching-animation.js";
    script.async = true;
    document.body.appendChild(script);
  }, [])

  return (
    <StyledSection id="hero">
      {!isIntroDone && <SplashScreen />}
      <StyledContentWrapper>
        <div className="video">
          <div id="lottie-searching"></div>
        </div>
        <div className="intro">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={gControls}
            data-testid="animated-heading"
          >
            <h1 className="title">
              {frontmatter.title}
            </h1>
            <h2 className="subtitlePrefix">{frontmatter.subtitle}</h2>
            <h2 className="subtitle">{frontmatter.description}</h2>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} animate={sControls}>
            <StyledButtons>
              <Link className="cta-btn" to="/signup/">
                sign up
              </Link>
              <a className="cta-btn empty" href="https://labs.hrflow.ai">
                See ai in action
              </a>
            </StyledButtons>
          </motion.div>
        </div>
      </StyledContentWrapper>
    </StyledSection>
  )
}

Hero.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      node: PropTypes.shape({
        frontmatter: PropTypes.object.isRequired,
      }).isRequired,
    }).isRequired
  ).isRequired,
}

export default Hero
